import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { appRoutes } from '@tekman/onmat/constants';
import { Feature, Grades, Rubric, RubricaInfo, RubricGrade } from '@tekman/onmat/models';
import { EnvironmentService, UserService } from '@tekman/onmat/service/generic';
import { EmojisRubric } from '@tekman/onmat/service/onmat';
import { GroupsFacade } from '@tekman/onmat/store/group';
import { ActivitiesFacade } from '@tekman/onmat/store/teacher/activities';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'rubricas-panel',
  templateUrl: './rubricas-panel.component.html',
  styleUrls: ['./rubricas-panel.component.scss'],
})
export class RubricasPanelComponent implements OnInit, OnChanges {
  static colored = '-colored';
  @Input() content: Rubric;
  @Input() correctionMode = false;
  @Input() isEvaluable: boolean;
  @Input() marks: Grades[];
  @Input() isEvaluated: boolean;
  @Output() openModal: EventEmitter<RubricGrade> = new EventEmitter<RubricGrade>();
  @Output() updatedGrades: EventEmitter<RubricGrade> = new EventEmitter<RubricGrade>();
  isTeacher = false;
  title = '';
  evaluationItemSubTitle = '';
  isShown = false;
  seeContent = false;
  evaluationIcons = new Map();
  evaluationNote = '';
  initEvaluationNote = '';
  rubricInfo: RubricaInfo;
  rubricInfoArray: RubricaInfo[] = [];
  featureEvaluable: Feature;
  appRoutes: typeof appRoutes = appRoutes;
  readonly icons: EmojisRubric[];
  isLomloe: boolean;

  constructor(
    private readonly groupsFacade: GroupsFacade,
    private readonly userService: UserService,
    public route: ActivatedRoute,
    public readonly activitiesFacade: ActivitiesFacade,
    private readonly env: EnvironmentService
  ) {
    this.evaluationIcons.set('0-5', EmojisRubric.BAD);
    this.evaluationIcons.set('5-7', EmojisRubric.REGULAR);
    this.evaluationIcons.set('7-9', EmojisRubric.GOOD);
    this.evaluationIcons.set('9-10', EmojisRubric.EXCELLENT);
  }

  ngOnInit(): void {
    this.isLomloe = this.env.settings?.isLomloeEnv;
    this.isTeacher = this.userService.isTeacher();
    this.initializeContent();
    this.updateNote();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['marks']) {
      this.evaluationNote = this.initEvaluationNote = this.trailDecimals(
        changes['marks'].currentValue
          ?.find(subcompetence => subcompetence.subCompetenceId === this.content.subCompetenceId)
          .grade.toString()
      );
      this.updateNote();
    }
    if (changes['proposedMarks'] && changes['proposedMarks'].currentValue) {
      this.evaluationNote = this.initEvaluationNote = this.trailDecimals(
        changes['proposedMarks'].currentValue
          ?.find(subcompetence => subcompetence.subCompetenceId === this.content.subCompetenceId)
          .grade.toString()
      );
      this.updateNote();
    }
  }

  initializeContent(): void {
    this.evaluationIcons.forEach((value, key) => {
      const startNote = key.split('-')[0];
      const endNote = key.split('-')[1];
      this.content?.rubricLevels.forEach((levels, index) => {
        if (levels.range.min === parseInt(startNote) && levels.range.max === parseInt(endNote)) {
          this.rubricInfo = { description: levels.description, icon: value, range: levels.range };
          this.rubricInfoArray[index] = this.rubricInfo;
        }
      });
    });
    this.title = this.content?.competenceTitle;
    this.evaluationItemSubTitle = this.content?.evaluationItemSubTitle;
    if ('grade' in this.content) {
      this.evaluationNote = this.initEvaluationNote = this.trailDecimals(this.content.grade?.toString());
    } else
      this.evaluationNote = this.initEvaluationNote = this.trailDecimals(
        this.marks?.find(subcompetence => subcompetence.subCompetenceId === this.content.subCompetenceId).grade.toString()
      );
  }

  coloredIcon(index: number): void {
    if (!this.rubricInfoArray[index].icon.includes(RubricasPanelComponent.colored)) {
      this.rubricInfoArray[index].icon = this.rubricInfoArray[index].icon.concat(RubricasPanelComponent.colored);
    }
  }

  uncoloredIcon(index: number): void {
    if (this.rubricInfoArray[index].icon.includes(RubricasPanelComponent.colored)) {
      this.rubricInfoArray[index].icon = this.rubricInfoArray[index].icon.replace(RubricasPanelComponent.colored, '');
    }
  }

  updateNote(): void {
    this.rubricInfoArray.forEach((element, index) => {
      this.coloredIcon(index);
    });
  }

  setNote(newNote: number): void {
    if (this.isTeacher && this.correctionMode) {
      if (newNote !== 10) {
        newNote = newNote - 1;
      }
      this.evaluationNote = this.trailDecimals(newNote.toString());
      this.updateNote();
      this.emitEvaluationGrades(this.content.subCompetenceId);
    }
  }

  toggleAll(): void {
    this.isShown = !this.isShown;
    this.seeContent = !this.seeContent;
    if (this.seeContent) this.scrollToResults();
  }

  scrollToResults() {
    setTimeout(() => {
      document.getElementById('content').scrollIntoView();
    });
  }

  groupHasStudents(): Observable<boolean> {
    return this.groupsFacade.students$.pipe(
      map(students => {
        return students.length > 1;
      })
    );
  }

  openAssessmentModal(subCompetenceId: string): void {
    this.openModal.emit({
      evaluationNote: this.trailDecimals(this.evaluationNote),
      subCompetenceId: subCompetenceId,
    });
  }

  emitEvaluationGrades(subCompetenceId: string): void {
    if (this.evaluationNote !== '') {
      this.evaluationNote = parseFloat(this.evaluationNote.replace(',', '.')).toLocaleString(this.getLocale(), {
        minimumFractionDigits: 2,
      });
    }
    if (this.initEvaluationNote !== this.evaluationNote) {
      this.updatedGrades.emit({
        evaluationNote: this.evaluationNote === '' ? null : this.evaluationNote,
        subCompetenceId: subCompetenceId,
      });
    }
    this.evaluationNote = this.evaluationNote === '' ? '-' : this.evaluationNote;
  }

  deleteHyphen(evaluationNote: string) {
    if (evaluationNote === '-') {
      this.evaluationNote = '';
    }
  }

  private getLocale(): string {
    return window.location.hostname.includes('mx') ? 'es-MX' : 'es-ES';
  }

  private trailDecimals(note: string): string {
    return note && note !== '-'
      ? this.getLocale() === 'es-ES'
        ? parseFloat(note.replace(',', '.')).toFixed(2).replace(/\.0+$/, '').replace('.', ',')
        : parseFloat(note.replace(',', '.')).toFixed(2).replace(/\.0+$/, '')
      : note;
  }
}
